<template>
  <div class="page-wrap">
    <div class="paper-layout">
      <el-card shadow="never" class="folder-panel">
        <div class="folder-header">
          <span class="folder-title">文件夹</span>
          <el-button type="text" icon="el-icon-plus" @click="openFolderDialog(null)">新建</el-button>
        </div>
        <div
          class="folder-item"
          :class="{ active: selectedFolderKey === 'all' }"
          @click="selectFolder('all')"
        >
          <i class="el-icon-files"></i>
          <span>全部试卷</span>
          <span class="folder-count">{{ folderSummary.totalCount || 0 }}</span>
        </div>
        <div
          class="folder-item"
          :class="{ active: selectedFolderKey === 'none' }"
          @click="selectFolder('none')"
        >
          <i class="el-icon-document"></i>
          <span>未分类</span>
          <span class="folder-count">{{ folderSummary.uncategorizedCount || 0 }}</span>
        </div>
        <el-tree
          v-if="folderTree.length"
          ref="folderTree"
          class="folder-tree"
          :data="folderTree"
          node-key="id"
          :props="{ label: 'name', children: 'children' }"
          highlight-current
          default-expand-all
          @node-click="onFolderNodeClick"
        >
          <span slot-scope="{ node, data }" class="tree-node">
            <i class="el-icon-folder"></i>
            <span class="tree-label">{{ node.label }}</span>
            <span class="folder-count">{{ data.paperCount || 0 }}</span>
          </span>
        </el-tree>
        <div v-else class="folder-empty">暂无文件夹，点击「新建」创建</div>
        <div v-if="selectedFolderId > 0" class="folder-actions">
          <el-button type="text" size="mini" @click="openFolderDialog(selectedFolderId)">子文件夹</el-button>
          <el-button type="text" size="mini" @click="renameFolder">重命名</el-button>
          <el-button type="text" size="mini" class="danger-text" @click="deleteFolder">删除</el-button>
        </div>
      </el-card>

      <div class="paper-main">
    <el-card shadow="never" class="me-card" v-if="me">
      <span>任教学科：<b>{{ me.subjectName || '未配置' }}</b></span>
      <span class="hint">可新建本学科试卷并组卷，提交后需管理员审核通过方可发布与分享。</span>
    </el-card>

    <el-alert
      type="info"
      show-icon
      :closable="false"
      class="guide-alert"
      title="如何组卷？"
    >
      点击右上角「新建试卷」创建试卷后，会自动进入组卷页，可从题库添加题目。列表中管理员发布的已发布试卷仅可「查看题目」；您自己创建的<strong>草稿</strong>或<strong>已驳回</strong>试卷会显示「组卷」按钮。
    </el-alert>

    <el-card shadow="never" class="filter-card">
      <div class="top-bar">
        <div class="filters">
          <el-select v-model="filterForm.schoolYearId" placeholder="学年" clearable style="width:130px" @change="loadList">
            <el-option v-for="y in schoolYears" :key="y.id" :label="y.name" :value="y.id" />
          </el-select>
          <el-select v-model="filterForm.gradeId" placeholder="年级" clearable style="width:110px" @change="onFilterGradeChange">
            <el-option v-for="g in grades" :key="g.id" :label="g.name" :value="g.id" />
          </el-select>
          <el-select v-model="filterForm.semesterId" placeholder="学期" clearable style="width:110px" @change="loadList">
            <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
          <el-select v-model="filterForm.examTypeId" placeholder="考试类型" clearable style="width:120px" @change="loadList">
            <el-option v-for="e in examTypes" :key="e.id" :label="e.name" :value="e.id" />
          </el-select>
          <el-select v-model="filterForm.status" placeholder="状态" clearable style="width:110px" @change="loadList">
            <el-option label="草稿" :value="0" />
            <el-option label="待审核" :value="2" />
            <el-option label="已驳回" :value="3" />
            <el-option label="已发布" :value="1" />
          </el-select>
        </div>
        <div class="top-actions">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreateDialog">新建试卷</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="loadList">刷新</el-button>
        </div>
      </div>
      <div v-if="currentFolderLabel" class="folder-breadcrumb">
        当前位置：<b>{{ currentFolderLabel }}</b>
      </div>
    </el-card>

    <div v-loading="loading" class="paper-list">
      <el-empty v-if="!loading && tableData.length === 0" description="暂无本学科试卷" />

      <div v-for="row in tableData" :key="row.id" class="paper-card">
        <div class="paper-header">
          <span class="paper-title">{{ row.title }}</span>
          <el-tag :type="paperStatusType(row.status)" size="small" style="margin-left:8px">
            {{ paperStatusLabel(row.status) }}
          </el-tag>
          <el-tag v-if="isOwnPaper(row)" size="mini" type="info" style="margin-left:4px">我创建的</el-tag>
          <el-tag v-else-if="me" size="mini" type="warning" style="margin-left:4px">仅可查看</el-tag>
          <div class="paper-meta">
            <span v-if="getDictName(schoolYears, row.schoolYearId)" class="meta-chip">{{ getDictName(schoolYears, row.schoolYearId) }}</span>
            <span v-if="getDictName(grades, row.gradeId)" class="meta-chip">{{ getDictName(grades, row.gradeId) }}</span>
            <span v-if="getDictName(semesters, row.semesterId)" class="meta-chip">{{ getDictName(semesters, row.semesterId) }}</span>
            <span v-if="getDictName(examTypes, row.examTypeId)" class="meta-chip">{{ getDictName(examTypes, row.examTypeId) }}</span>
            <span class="meta-chip q-count">{{ row.questionCount }} 题</span>
            <span class="meta-chip">ID: {{ row.id }}</span>
          </div>
        </div>
        <div v-if="row.description" class="paper-desc">{{ row.description }}</div>
        <div v-if="row.status === 3 && row.auditRemark" class="paper-audit-remark">驳回：{{ row.auditRemark }}</div>
        <div class="paper-footer">
          <span class="paper-time">{{ formatTime(row.createdAt) }}</span>
          <div class="paper-actions">
            <el-dropdown v-if="canMove(row)" trigger="click" @command="cmd => movePaper(row, cmd)">
              <el-button type="text" icon="el-icon-folder">移动到</el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :command="0">未分类</el-dropdown-item>
                <el-dropdown-item v-for="f in folderFlat" :key="f.id" :command="f.id">{{ f.displayName || f.name }}</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-divider direction="vertical" />
            <el-button type="text" :icon="canEdit(row) ? 'el-icon-edit-outline' : 'el-icon-view'" @click="goDetail(row.id)">
              {{ canEdit(row) ? '组卷' : '查看题目' }}
            </el-button>
            <el-button
              v-if="!isOwnPaper(row)"
              type="text"
              icon="el-icon-document-copy"
              @click="copyPaperId(row)"
            >复制ID</el-button>
            <template v-if="canEdit(row)">
              <el-divider direction="vertical" />
              <el-button type="text" icon="el-icon-setting" @click="openEditDialog(row)">编辑信息</el-button>
            </template>
            <template v-if="canEdit(row) && row.questionCount > 0">
              <el-divider direction="vertical" />
              <el-button type="text" icon="el-icon-s-promotion" @click="submitReview(row)">提交审核</el-button>
            </template>
            <el-divider direction="vertical" />
            <el-button type="text" icon="el-icon-share" :disabled="row.status !== 1" @click="openShare(row)">分享</el-button>
          </div>
        </div>
      </div>
    </div>

    <el-pagination
      class="pagination"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
      :current-page="page + 1"
      :page-sizes="[10, 20, 50]"
      :page-size="size"
      :total="total"
      layout="total, sizes, prev, pager, next"
    />
      </div>
    </div>

    <el-dialog :title="folderDialogTitle" :visible.sync="folderDialogVisible" width="400px">
      <el-input v-model="folderForm.name" placeholder="请输入文件夹名称" maxlength="50" show-word-limit />
      <template #footer>
        <el-button @click="folderDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="folderSaving" @click="saveFolder">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog title="分享试卷" :visible.sync="shareVisible" width="400px">
      <div v-if="sharePaper.id" class="share-content">
        <div class="share-paper-info">
          <h4>{{ sharePaper.title }}</h4>
          <p>{{ sharePaper.questionCount }} 道题</p>
        </div>
        <div class="share-section">
          <label>分享链接</label>
          <div class="share-link-box">
            <el-input v-model="shareLink" readonly size="small" />
            <el-button type="primary" size="small" @click="copyLink">复制</el-button>
          </div>
        </div>
        <div class="share-section">
          <label>二维码</label>
          <div class="qr-code-box">
            <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="二维码" />
            <div v-else class="qr-placeholder">加载中...</div>
          </div>
        </div>
      </div>
    </el-dialog>

    <el-dialog :title="editMode ? '编辑试卷' : '新建试卷'" :visible.sync="dialogVisible" width="600px" @closed="resetForm">
      <el-form :model="editForm" :rules="formRules" ref="editForm" label-width="80px">
        <el-form-item label="任教学科">
          <el-input :value="me && me.subjectName" disabled />
        </el-form-item>
        <el-form-item label="试卷名称" prop="title">
          <el-input v-model="editForm.title" placeholder="请选择下方条件自动生成试卷名称" />
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="学年" prop="schoolYearId">
              <el-select v-model="editForm.schoolYearId" placeholder="必选" style="width:100%" @change="generatePaperTitle">
                <el-option v-for="y in schoolYears" :key="y.id" :label="y.name" :value="y.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="年级" prop="gradeId">
              <el-select v-model="editForm.gradeId" placeholder="必选" style="width:100%" @change="onEditGradeChange">
                <el-option v-for="g in grades" :key="g.id" :label="g.name" :value="g.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="学期" prop="semesterId">
              <el-select v-model="editForm.semesterId" placeholder="必选" style="width:100%" @change="generatePaperTitle">
                <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="考试类型" prop="examTypeId">
              <el-select v-model="editForm.examTypeId" placeholder="必选" style="width:100%" @change="generatePaperTitle">
                <el-option v-for="e in examTypes" :key="e.id" :label="e.name" :value="e.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="试卷说明">
          <el-input type="textarea" v-model="editForm.description" :rows="3" placeholder="可选" />
        </el-form-item>
        <el-form-item v-if="!editMode" label="引用试卷">
          <el-input
            v-model="editForm.sourcePaperId"
            clearable
            placeholder="可选：粘贴他人试卷 ID，将导入其题目"
          />
          <div class="form-tip">在他人试卷行点击「复制ID」，粘贴到此处后创建即可生成自己的可编辑副本。</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="savePaper">{{ editMode ? '保存' : '创建并组卷' }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import teacherRequest from '../../utils/teacherRequest'
import { canTeacherEditPaper, canTeacherMovePaper, isTeacherOwnPaper } from '../../utils/teacherOwnership'

export default {
  name: 'TeacherPaperList',
  data() {
    return {
      me: null,
      filterForm: { schoolYearId: null, gradeId: null, semesterId: null, examTypeId: null, status: null },
      schoolYears: [],
      grades: [],
      semesters: [],
      examTypes: [],
      tableData: [],
      page: 0,
      size: 10,
      total: 0,
      loading: false,
      shareVisible: false,
      sharePaper: {},
      shareLink: '',
      qrCodeUrl: '',
      dialogVisible: false,
      editMode: false,
      saving: false,
      editForm: { id: null, title: '', description: '', schoolYearId: null, gradeId: null, semesterId: null, examTypeId: null, sourcePaperId: '' },
      dictLookup: {},
      formRules: {
        title: [{ required: true, message: '请输入试卷名称', trigger: 'blur' }],
        schoolYearId: [{ required: true, message: '请选择学年', trigger: 'change' }],
        gradeId: [{ required: true, message: '请选择年级', trigger: 'change' }],
        semesterId: [{ required: true, message: '请选择学期', trigger: 'change' }],
        examTypeId: [{ required: true, message: '请选择考试类型', trigger: 'change' }]
      },
      folderTree: [],
      folderFlat: [],
      folderSummary: { totalCount: 0, uncategorizedCount: 0 },
      selectedFolderKey: 'all',
      selectedFolderId: null,
      folderDialogVisible: false,
      folderDialogMode: 'create',
      folderForm: { name: '', parentId: 0 },
      folderSaving: false
    }
  },
  computed: {
    currentFolderLabel() {
      if (this.selectedFolderKey === 'all') return '全部试卷'
      if (this.selectedFolderKey === 'none') return '未分类'
      const f = this.folderFlat.find(x => x.id === this.selectedFolderId)
      return f ? f.name.trim() : ''
    },
    folderDialogTitle() {
      return this.folderDialogMode === 'rename' ? '重命名文件夹' : '新建文件夹'
    },
    createFolderId() {
      if (this.selectedFolderId > 0) return this.selectedFolderId
      return null
    }
  },
  created() {
    this.loadMe()
    this.loadDicts()
    this.loadStageDictBundle(null)
    this.loadDictLookup()
    this.loadFolders()
    this.loadList()
  },
  methods: {
    loadMe() {
      teacherRequest.get('/me').then(data => { this.me = data }).catch(() => {})
    },
    loadDict(type, target, stageId) {
      const params = { type }
      if (stageId != null && stageId !== '') {
        params.stageId = stageId
      }
      return teacherRequest.get('/meta/dict', { params }).then(data => {
        let list = data || []
        if (stageId == null && ['exam_type', 'semester', 'school_year'].includes(type)) {
          const seen = new Set()
          list = list.filter(item => {
            if (!item || !item.name || seen.has(item.name)) return false
            seen.add(item.name)
            return true
          })
        }
        this[target] = list
      }).catch(() => {})
    },
    loadStageDictBundle(stageId) {
      const scopedStageId = stageId != null && stageId !== '' ? stageId : null
      return Promise.all([
        this.loadDict('school_year', 'schoolYears', scopedStageId),
        this.loadDict('semester', 'semesters', scopedStageId),
        this.loadDict('exam_type', 'examTypes', scopedStageId)
      ])
    },
    syncStageDictsByGradeId(gradeId) {
      const grade = (this.grades || []).find(g => g.id === gradeId)
      const stageId = grade ? grade.stageId : null
      return this.loadStageDictBundle(stageId)
    },
    remapFilterDictByName(names = {}) {
      const schoolYearName = names.schoolYearName || this.getDictName(this.schoolYears, this.filterForm.schoolYearId)
      const semesterName = names.semesterName || this.getDictName(this.semesters, this.filterForm.semesterId)
      const examTypeName = names.examTypeName || this.getDictName(this.examTypes, this.filterForm.examTypeId)
      if (schoolYearName) {
        const y = this.schoolYears.find(x => x.name === schoolYearName)
        this.filterForm.schoolYearId = y ? y.id : null
      }
      if (semesterName) {
        const s = this.semesters.find(x => x.name === semesterName)
        this.filterForm.semesterId = s ? s.id : null
      }
      if (examTypeName) {
        const e = this.examTypes.find(x => x.name === examTypeName)
        this.filterForm.examTypeId = e ? e.id : null
      }
    },
    remapEditDictByName(names = {}) {
      const schoolYearName = names.schoolYearName || this.getDictName(this.schoolYears, this.editForm.schoolYearId)
      const semesterName = names.semesterName || this.getDictName(this.semesters, this.editForm.semesterId)
      const examTypeName = names.examTypeName || this.getDictName(this.examTypes, this.editForm.examTypeId)
      if (schoolYearName) {
        const y = this.schoolYears.find(x => x.name === schoolYearName)
        this.editForm.schoolYearId = y ? y.id : null
      }
      if (semesterName) {
        const s = this.semesters.find(x => x.name === semesterName)
        this.editForm.semesterId = s ? s.id : null
      }
      if (examTypeName) {
        const e = this.examTypes.find(x => x.name === examTypeName)
        this.editForm.examTypeId = e ? e.id : null
      }
    },
    onFilterGradeChange(gradeId) {
      const names = {
        schoolYearName: this.getDictName(this.schoolYears, this.filterForm.schoolYearId),
        semesterName: this.getDictName(this.semesters, this.filterForm.semesterId),
        examTypeName: this.getDictName(this.examTypes, this.filterForm.examTypeId)
      }
      this.syncStageDictsByGradeId(gradeId).then(() => {
        this.remapFilterDictByName(names)
        this.loadList()
      })
    },
    onEditGradeChange() {
      const names = {
        schoolYearName: this.getDictName(this.schoolYears, this.editForm.schoolYearId),
        semesterName: this.getDictName(this.semesters, this.editForm.semesterId),
        examTypeName: this.getDictName(this.examTypes, this.editForm.examTypeId)
      }
      this.syncStageDictsByGradeId(this.editForm.gradeId).then(() => {
        this.remapEditDictByName(names)
        this.generatePaperTitle()
      })
    },
    loadDicts() {
      teacherRequest.get('/meta/grades').then(data => { this.grades = data || [] }).catch(() => {})
    },
    loadFolders() {
      teacherRequest.get('/papers/folder/tree').then(data => {
        this.folderTree = data.folders || []
        this.folderSummary = data.summary || { totalCount: 0, uncategorizedCount: 0 }
      }).catch(() => {})
      teacherRequest.get('/papers/folder/flat').then(data => {
        this.folderFlat = data || []
      }).catch(() => {})
    },
    listFolderIdParam() {
      if (this.selectedFolderKey === 'all') return undefined
      if (this.selectedFolderKey === 'none') return 0
      return this.selectedFolderId
    },
    selectFolder(key, folderId) {
      this.selectedFolderKey = key
      this.selectedFolderId = folderId || null
      this.page = 0
      if (this.$refs.folderTree) {
        this.$refs.folderTree.setCurrentKey(null)
      }
      this.loadList()
    },
    onFolderNodeClick(data) {
      this.selectedFolderKey = String(data.id)
      this.selectedFolderId = data.id
      this.page = 0
      this.loadList()
    },
    openFolderDialog(parentId) {
      this.folderDialogMode = 'create'
      this.folderForm = { name: '', parentId: parentId || 0 }
      this.folderDialogVisible = true
    },
    renameFolder() {
      if (!this.selectedFolderId) return
      const f = this.folderFlat.find(x => x.id === this.selectedFolderId)
      this.folderDialogMode = 'rename'
      this.folderForm = { name: f ? (f.name || '').trim() : '', id: this.selectedFolderId }
      this.folderDialogVisible = true
    },
    saveFolder() {
      if (!this.folderForm.name || !this.folderForm.name.trim()) {
        this.$message.warning('请输入文件夹名称')
        return
      }
      this.folderSaving = true
      const req = this.folderDialogMode === 'rename'
        ? teacherRequest.put(`/papers/folder/${this.folderForm.id}`, { name: this.folderForm.name.trim() })
        : teacherRequest.post('/papers/folder', { name: this.folderForm.name.trim(), parentId: this.folderForm.parentId || 0 })
      req.then(() => {
        this.$message.success(this.folderDialogMode === 'rename' ? '已重命名' : '文件夹已创建')
        this.folderDialogVisible = false
        this.loadFolders()
      }).finally(() => { this.folderSaving = false })
    },
    deleteFolder() {
      if (!this.selectedFolderId) return
      this.$confirm('删除后，该文件夹内的试卷将移至「未分类」，确认删除？', '提示', { type: 'warning' }).then(() => {
        teacherRequest.delete(`/papers/folder/${this.selectedFolderId}`).then(() => {
          this.$message.success('已删除')
          this.selectFolder('all')
          this.loadFolders()
        })
      }).catch(() => {})
    },
    movePaper(row, folderId) {
      teacherRequest.put(`/papers/folder/move-paper/${row.id}`, { folderId: folderId || null }).then(() => {
        this.$message.success('已移动')
        this.loadList()
        this.loadFolders()
      })
    },
    loadList() {
      this.loading = true
      const { schoolYearId, gradeId, semesterId, examTypeId, status } = this.filterForm
      const folderId = this.listFolderIdParam()
      const params = { page: this.page, size: this.size, schoolYearId, gradeId, semesterId, examTypeId, status }
      if (folderId !== undefined) params.folderId = folderId
      teacherRequest.get('/papers/list', { params }).then(data => {
        this.tableData = data.content || []
        this.total = data.totalElements || 0
      }).catch(() => {
        this.tableData = []
        this.total = 0
      }).finally(() => { this.loading = false })
    },
    handlePageChange(val) { this.page = val - 1; this.loadList() },
    handleSizeChange(val) { this.size = val; this.page = 0; this.loadList() },
    loadDictLookup() {
      const types = ['school_year', 'semester', 'exam_type', 'grade', 'subject']
      return Promise.all(
        types.map(type => teacherRequest.get('/meta/dict', { params: { type } }))
      ).then(results => {
        const lookup = {}
        results.flat().forEach(item => {
          if (item && item.id != null) lookup[item.id] = item.name
        })
        this.dictLookup = lookup
      }).catch(() => {})
    },
    getDictName(list, id) {
      const item = (list || []).find(x => x.id === id)
      if (item) return item.name
      if (id == null || id === '') return ''
      return this.dictLookup[id] || ''
    },
    formatTime(t) {
      if (!t) return ''
      return String(t).replace('T', ' ').substring(0, 16)
    },
    paperStatusLabel(status) {
      const map = { 0: '草稿', 1: '已发布', 2: '待审核', 3: '已驳回' }
      return map[status] || '未知'
    },
    paperStatusType(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'warning'
      if (status === 3) return 'danger'
      return 'info'
    },
    isOwnPaper(row) {
      return isTeacherOwnPaper(row, this.me)
    },
    canEdit(row) {
      return canTeacherEditPaper(row, this.me)
    },
    canMove(row) {
      return canTeacherMovePaper(row, this.me)
    },
    copyPaperId(row) {
      const text = String(row.id)
      const done = () => this.$message.success(`已复制试卷 ID：${text}`)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
          this.fallbackCopyText(text)
          done()
        })
        return
      }
      this.fallbackCopyText(text)
      done()
    },
    fallbackCopyText(text) {
      const input = document.createElement('textarea')
      input.value = text
      input.style.position = 'fixed'
      input.style.opacity = '0'
      document.body.appendChild(input)
      input.select()
      try { document.execCommand('copy') } catch (e) {}
      document.body.removeChild(input)
    },
    openCreateDialog() {
      this.editMode = false
      this.editForm = { id: null, title: '', description: '', schoolYearId: null, gradeId: null, semesterId: null, examTypeId: null, sourcePaperId: '' }
      this.dialogVisible = true
    },
    openEditDialog(row) {
      this.editMode = true
      this.editForm = {
        id: row.id,
        title: row.title,
        description: row.description || '',
        schoolYearId: row.schoolYearId,
        gradeId: row.gradeId,
        semesterId: row.semesterId,
        examTypeId: row.examTypeId
      }
      if (row.gradeId) {
        this.syncStageDictsByGradeId(row.gradeId)
      }
      this.dialogVisible = true
    },
    generatePaperTitle() {
      const { schoolYearId, gradeId, semesterId, examTypeId } = this.editForm
      const parts = []
      if (schoolYearId) parts.push(this.getDictName(this.schoolYears, schoolYearId))
      if (gradeId) parts.push(this.getDictName(this.grades, gradeId))
      if (semesterId) parts.push(this.getDictName(this.semesters, semesterId))
      if (this.me && this.me.subjectName) parts.push(this.me.subjectName)
      if (examTypeId) parts.push(this.getDictName(this.examTypes, examTypeId))
      if (parts.length) this.editForm.title = parts.join('') + '试卷'
    },
    resetForm() {
      this.$refs.editForm && this.$refs.editForm.resetFields()
    },
    savePaper() {
      this.$refs.editForm.validate(valid => {
        if (!valid) return
        this.saving = true
        const payload = {
          title: this.editForm.title,
          description: this.editForm.description,
          schoolYearId: this.editForm.schoolYearId,
          gradeId: this.editForm.gradeId,
          semesterId: this.editForm.semesterId,
          examTypeId: this.editForm.examTypeId
        }
        if (!this.editMode && this.createFolderId) {
          payload.folderId = this.createFolderId
        }
        if (!this.editMode && payload.sourcePaperId) {
          const sourceId = Number(String(payload.sourcePaperId).trim())
          if (!sourceId || Number.isNaN(sourceId)) {
            this.$message.warning('引用试卷 ID 格式不正确')
            this.saving = false
            return
          }
          payload.sourcePaperId = sourceId
        } else {
          delete payload.sourcePaperId
        }
        const req = this.editMode
          ? teacherRequest.put(`/papers/${this.editForm.id}`, payload)
          : teacherRequest.post('/papers', payload)
        req.then(data => {
          this.$message.success(this.editMode ? '修改成功' : (payload.sourcePaperId ? '创建成功并已导入引用题目' : '创建成功'))
          this.dialogVisible = false
          this.loadList()
          this.loadFolders()
          if (!this.editMode && data && data.id) {
            this.$router.push('/teacher/papers/' + data.id)
          }
        }).finally(() => { this.saving = false })
      })
    },
    submitReview(row) {
      if (!row.questionCount) {
        this.$message.warning('请先组卷添加题目')
        return
      }
      this.$confirm('提交后需管理员审核通过方可发布，确认提交？', '提交审核', { type: 'warning' }).then(() => {
        teacherRequest.post(`/papers/${row.id}/submit-review`).then(() => {
          this.$message.success('已提交审核')
          this.loadList()
        })
      }).catch(() => {})
    },
    goDetail(id) {
      this.$router.push('/teacher/papers/' + id)
    },
    openShare(row) {
      if (row.status !== 1) {
        this.$message.warning('仅已发布试卷可分享')
        return
      }
      this.sharePaper = row
      this.shareVisible = true
      this.shareLink = ''
      this.qrCodeUrl = ''
      teacherRequest.get(`/papers/${row.id}/share`).then(data => {
        this.shareLink = data.link
        this.qrCodeUrl = data.qrCodeUrl
      })
    },
    copyLink() {
      navigator.clipboard.writeText(this.shareLink).then(() => {
        this.$message.success('链接已复制')
      })
    }
  }
}
</script>

<style scoped>
.paper-layout { display: flex; gap: 16px; align-items: flex-start; }
.folder-panel { width: 220px; flex-shrink: 0; }
.folder-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.folder-title { font-size: 14px; font-weight: 600; color: #333; }
.folder-item { display: flex; align-items: center; gap: 6px; padding: 8px 10px; border-radius: 6px; cursor: pointer; font-size: 13px; color: #555; margin-bottom: 4px; }
.folder-item:hover { background: #f5f7fa; }
.folder-item.active { background: #e6f4ff; color: #1677ff; }
.folder-count { margin-left: auto; font-size: 12px; color: #999; }
.folder-tree { margin-top: 4px; }
.tree-node { display: flex; align-items: center; gap: 4px; flex: 1; font-size: 13px; padding-right: 4px; }
.tree-label { flex: 1; overflow: hidden; text-overflow: ellipsis; }
.folder-empty { font-size: 12px; color: #bbb; padding: 8px 4px; line-height: 1.5; }
.folder-actions { margin-top: 10px; padding-top: 10px; border-top: 1px solid #f0f0f0; display: flex; flex-wrap: wrap; gap: 4px; }
.paper-main { flex: 1; min-width: 0; }
.folder-breadcrumb { margin-top: 10px; font-size: 13px; color: #6b7280; }
.danger-text { color: #ff4d4f; }
.me-card { margin-bottom: 16px; font-size: 14px; }
.guide-alert { margin-bottom: 16px; }
.me-card .hint { margin-left: 16px; color: #888; font-size: 13px; }
.top-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.top-actions { display: flex; gap: 8px; align-items: center; }
.filters { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.filter-card { margin-bottom: 16px; }
.paper-list { display: flex; flex-direction: column; gap: 10px; }
.paper-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 16px;
}
.paper-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.08); border-color: #d0e6ff; }
.paper-header { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.paper-title { font-size: 15px; font-weight: 600; color: #222; }
.paper-meta { display: flex; gap: 4px; margin-left: 8px; flex-wrap: wrap; }
.meta-chip { padding: 1px 8px; background: #f5f5f5; border-radius: 3px; font-size: 12px; color: #777; }
.meta-chip.q-count { background: #e6f4ff; color: #1677ff; }
.paper-desc { font-size: 13px; color: #888; margin-bottom: 8px; line-height: 1.5; }
.paper-audit-remark { font-size: 12px; color: #cf1322; margin-bottom: 8px; }
.paper-footer { display: flex; align-items: center; justify-content: space-between; border-top: 1px solid #f5f5f5; padding-top: 8px; margin-top: 4px; }
.paper-time { font-size: 12px; color: #bbb; }
.paper-actions { display: flex; align-items: center; flex-wrap: wrap; }
.paper-actions .el-divider--vertical { margin: 0 2px; }
.form-tip { margin-top: 6px; font-size: 12px; color: #9ca3af; line-height: 1.5; }
.pagination { margin-top: 16px; text-align: right; }
.share-paper-info h4 { margin: 0 0 4px; }
.share-section { margin-top: 16px; }
.share-section label { display: block; font-size: 13px; color: #666; margin-bottom: 6px; }
.share-link-box { display: flex; gap: 8px; }
.qr-code-box { text-align: center; margin-top: 8px; }
.qr-code-box img { width: 180px; height: 180px; }
.qr-placeholder { color: #999; padding: 40px; }
</style>
